import React, { useState, useEffect } from 'react';
import styles from './Sourcing.module.css';
import { 
  getSportSchema, 
  searchProspects, 
  analyzeRosterGaps,
  exportData 
} from '../utils/sourcingApi';

// Mock imports to avoid dependency issues
const mockComponents = {
  Card: ({ children, title, className }) => (
    <div className={`card ${className || ''}`} style={{ border: '1px solid #e8e8e8', borderRadius: '4px', padding: '16px', marginBottom: '16px' }}>
      {title && <h3 style={{ marginBottom: '16px' }}>{title}</h3>}
      {children}
    </div>
  ),
  Button: ({ children, onClick, className, disabled }) => (
    <button 
      onClick={onClick} 
      disabled={disabled}
      className={className}
      style={{ 
        padding: '8px 16px', 
        backgroundColor: disabled ? '#f5f5f5' : '#1890ff', 
        color: disabled ? '#d9d9d9' : 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        margin: '4px'
      }}
    >
      {children}
    </button>
  ),
  Select: ({ children, onChange, value, style }) => (
    <select 
      onChange={e => onChange(e.target.value)}
      value={value}
      style={{ ...style, padding: '8px', borderRadius: '4px', border: '1px solid #d9d9d9' }}
    >
      {children}
    </select>
  ),
  Option: ({ children, value }) => <option value={value}>{children}</option>,
  Tabs: ({ children, defaultActiveKey }) => (
    <div className="tabs">
      <div className="tab-content" style={{ marginTop: '16px' }}>
        {React.Children.toArray(children).find(child => child.key === defaultActiveKey)}
      </div>
    </div>
  ),
  TabPane: ({ children, tab, key }) => <div key={key}>{children}</div>,
  // Mock notification to avoid undefined errors
  notification: {
    error: ({ message, description }) => {
      console.error(`${message}: ${description}`);
    }
  },
  // Mock Modal to avoid undefined errors
  Modal: {
    info: ({ title, width, content }) => {
      console.info(`Modal: ${title}`);
      return null;
    }
  }
};

// Use our mock components
const { Card, Button, Select, Tabs, TabPane, Option, notification, Modal } = mockComponents;

const Sourcing = () => {
  // State management
  const [loading, setLoading] = useState(false);
  const [currentSport, setCurrentSport] = useState('baseball');
  const [viewMode, setViewMode] = useState('table');
  const [searchCriteria, setSearchCriteria] = useState({});
  const [results, setResults] = useState([]);
  const [sportSchema, setSportSchema] = useState(null);
  const [rosterGapAnalysis, setRosterGapAnalysis] = useState(null);
  const [weightedAttributes, setWeightedAttributes] = useState({});
  const [exportFormat, setExportFormat] = useState('csv');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [currentRoster, setCurrentRoster] = useState([]);
  const [teamRequirements, setTeamRequirements] = useState({});

  // Fetch sport-specific schema on sport change
  useEffect(() => {
    fetchSchema();
  }, [currentSport]);

  // Fetch schema
  const fetchSchema = async () => {
    try {
      setLoading(true);
      const schema = await getSportSchema(currentSport);
      setSportSchema(schema);
      
      // Initialize weighted attributes
      const initialWeights = {};
      schema.metrics.basic.forEach(metric => {
        initialWeights[metric] = 1;
      });
      setWeightedAttributes(initialWeights);
    } catch (error) {
      notification.error({
        message: 'Error',
        description: 'Failed to fetch sport schema'
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle search submission
  const handleSearch = async () => {
    try {
      setLoading(true);
      const searchResults = await searchProspects(currentSport, {
        ...searchCriteria,
        weights: weightedAttributes
      }, {
        limit: 50,
        sort: { [sportSchema?.metrics?.basic[0]]: -1 }
      });
      setResults(searchResults);
    } catch (error) {
      notification.error({
        message: 'Error',
        description: 'Failed to search prospects'
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle roster gap analysis
  const handleRosterAnalysis = async () => {
    try {
      setLoading(true);
      const analysis = await analyzeRosterGaps(currentSport, currentRoster, teamRequirements);
      setRosterGapAnalysis(analysis);
    } catch (error) {
      notification.error({
        message: 'Error',
        description: 'Failed to analyze roster gaps'
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle data export
  const handleExport = async () => {
    try {
      setLoading(true);
      const exportedData = await exportData(currentSport, results, exportFormat);
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([exportedData]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `sourcing_export_${Date.now()}.${exportFormat}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      notification.error({
        message: 'Error',
        description: 'Failed to export data'
      });
    } finally {
      setLoading(false);
    }
  };

  // Generate table columns
  const generateColumns = () => {
    if (!sportSchema) return [];

    const columns = [
      {
        title: 'Name',
        dataIndex: 'Name',
        key: 'name',
        fixed: 'left',
        width: 150,
        render: (text, record) => (
          <Button type="link" onClick={() => showProspectDetails(record)}>
            {text}
          </Button>
        )
      },
      {
        title: 'Position',
        dataIndex: 'Position',
        key: 'position',
        width: 100,
        filters: sportSchema.positions.map(pos => ({
          text: pos,
          value: pos
        })),
        onFilter: (value, record) => record.position === value
      }
    ];

    // Add basic metrics
    sportSchema.metrics.basic.forEach(metric => {
      columns.push({
        title: metric,
        dataIndex: metric,
        key: metric,
        width: 100,
        sorter: (a, b) => (a.stats?.[metric.toLowerCase()] || 0) - (b.stats?.[metric.toLowerCase()] || 0)
      });
    });

    // Add advanced metrics
    sportSchema.metrics.advanced.forEach(metric => {
      columns.push({
        title: metric,
        dataIndex: ['stats', metric.toLowerCase()],
        key: metric,
        width: 100,
        sorter: (a, b) => (a.stats?.[metric.toLowerCase()] || 0) - (b.stats?.[metric.toLowerCase()] || 0)
      });
    });

    return columns;
  };

  // Show prospect details modal
  const showProspectDetails = (prospect) => {
    Modal.info({
      title: prospect.name,
      width: 800,
      content: (
        <div className={styles['prospect-details']}>
          <div className={styles['basic-info']}>
            <h3>Basic Information</h3>
            <p>Position: {prospect.position}</p>
            <p>Team: {prospect.team}</p>
            <p>Year: {prospect.year}</p>
          </div>
          <div className={styles.stats}>
            <h3>Statistics</h3>
            <div className={styles['stats-grid']}>
              {Object.entries(prospect.stats || {}).map(([key, value]) => (
                <div key={key} className={styles['stat-item']}>
                  <span className={styles['stat-label']}>{key}:</span>
                  <span className={styles['stat-value']}>{value}</span>
                </div>
              ))}
            </div>
          </div>
          {prospect.analysis && (
            <div className={styles.analysis}>
              <h3>Analysis</h3>
              <p>{prospect.analysis}</p>
            </div>
          )}
        </div>
      )
    });
  };

  // Render filters (simplified to avoid dependency issues)
  const renderFilters = () => {
    if (!sportSchema) return null;

    return (
      <Card title="Search Filters">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '8px' }}>Sport</label>
            <Select
              style={{ width: 200 }}
              value={currentSport}
              onChange={setCurrentSport}
            >
              <Option value="baseball">Baseball</Option>
              <Option value="basketball">Basketball</Option>
              <Option value="football">Football</Option>
            </Select>
          </div>

          {sportSchema.positions && (
            <div>
              <label style={{ display: 'block', marginBottom: '8px' }}>Positions</label>
              <select
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d9d9d9' }}
                onChange={e => {
                  const options = e.target.options;
                  const positions = [];
                  for (let i = 0; i < options.length; i++) {
                    if (options[i].selected) {
                      positions.push(options[i].value);
                    }
                  }
                  setSearchCriteria({ ...searchCriteria, positions });
                }}
                multiple
              >
                {sportSchema.positions.map(pos => (
                  <option key={pos} value={pos}>{pos}</option>
                ))}
              </select>
            </div>
          )}

          <Button
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
          >
            {showAdvancedFilters ? 'Hide Advanced Filters' : 'Show Advanced Filters'}
          </Button>

          {showAdvancedFilters && (
            <Card title="Attribute Weights">
              {sportSchema.metrics?.basic?.map(metric => (
                <div key={metric} style={{ marginBottom: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span>{metric}</span>
                    <span>{weightedAttributes[metric]}</span>
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={5}
                    step={0.1}
                    value={weightedAttributes[metric]}
                    onChange={e => setWeightedAttributes({
                      ...weightedAttributes,
                      [metric]: parseFloat(e.target.value)
                    })}
                    style={{ width: '100%' }}
                  />
                </div>
              ))}
            </Card>
          )}

          <div style={{ display: 'flex', gap: '8px' }}>
            <Button
              onClick={handleSearch}
              disabled={loading}
            >
              {loading ? 'Searching...' : 'Search'}
            </Button>
            <Button
              onClick={handleExport}
              disabled={!results.length}
            >
              Export
            </Button>
          </div>
        </div>
      </Card>
    );
  };

  // Render results (simplified to avoid dependency issues)
  const renderResults = () => {
    if (!results.length) return null;

    switch (viewMode) {
      case 'table':
        return (
          <div className={styles['table-container']} style={{ border: '1px solid #e8e8e8', borderRadius: '4px', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#fafafa' }}>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e8e8e8' }}>Name</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e8e8e8' }}>Position</th>
                  {sportSchema?.metrics?.basic?.map(metric => (
                    <th key={metric} style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e8e8e8' }}>{metric}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {results.slice(0, 10).map((prospect, index) => (
                  <tr key={index} style={{ borderBottom: '1px solid #e8e8e8' }}>
                    <td style={{ padding: '12px' }}>
                      <Button onClick={() => alert(`Viewing details for ${prospect.Name}`)}>
                        {prospect.Name}
                      </Button>
                    </td>
                    <td style={{ padding: '12px' }}>{prospect.Position}</td>
                    {sportSchema?.metrics?.basic?.map(metric => (
                      <td key={metric} style={{ padding: '12px' }}>{prospect[metric]}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      case 'cards':
        return (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '16px' }}>
            {results.slice(0, 9).map((prospect, index) => (
              <Card 
                key={index} 
                title={prospect.Name}
              >
                <p><strong>Name:</strong> {prospect.Name}</p>
                <p><strong>Position:</strong> {prospect.Position}</p>
                <p><strong>Team:</strong> {prospect.team}</p>
                <div>
                  {sportSchema?.metrics?.basic?.slice(0, 3).map(metric => (
                    <p key={metric}>
                      <strong>{metric}:</strong> {prospect[metric]}
                    </p>
                  ))}
                </div>
                <Button onClick={() => alert(`Viewing details for ${prospect.Name}`)}>
                  View Details
                </Button>
              </Card>
            ))}
          </div>
        );
      default:
        return null;
    }
  };

  // Render roster gap visualization (simplified to avoid dependency issues)
  const renderGapAnalysis = () => {
    if (!rosterGapAnalysis) return null;

    const { positions, attributes } = rosterGapAnalysis || { positions: {}, attributes: {} };
    
    return (
      <div className={styles['gap-analysis']}>
        <Card title="Position Gaps">
          <div className={styles['visualization-container']}>
            <div style={{ padding: '20px', textAlign: 'center', backgroundColor: '#f5f5f5' }}>
              <p>Position gap visualization (chart visualization requires @nivo/bar)</p>
              <p>Data: {JSON.stringify(Object.keys(positions))}</p>
            </div>
          </div>
        </Card>

        <Card title="Attribute Analysis" className={styles['mt-4']}>
          <div className={styles['visualization-container']}>
            <div style={{ padding: '20px', textAlign: 'center', backgroundColor: '#f5f5f5' }}>
              <p>Attribute analysis visualization (chart visualization requires @nivo/radar)</p>
              <p>Data: {JSON.stringify(Object.keys(attributes))}</p>
            </div>
          </div>
        </Card>
      </div>
    );
  };

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1 style={{ margin: 0 }}>Prospect Sourcing</h1>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button
            onClick={() => setViewMode(viewMode === 'table' ? 'cards' : 'table')}
          >
            {viewMode === 'table' ? 'Card View' : 'Table View'}
          </Button>
          <Button
            onClick={handleRosterAnalysis}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Analyze Roster Gaps'}
          </Button>
        </div>
      </div>

      <Tabs defaultActiveKey="search">
        <TabPane tab="Search" key="search">
          {renderFilters()}
          {renderResults()}
        </TabPane>
        <TabPane tab="Roster Gap Analysis" key="analysis">
          {renderGapAnalysis()}
        </TabPane>
      </Tabs>
    </div>
  );
};

export default Sourcing;